import { Badge } from "@zapier/design-system";
import type { ChatbotBlock } from "blocks/v2/Chatbot/schema";
import { BotMessage } from "./BotMessage";
import { BotResponse } from "./BotResponse";
import { UserMessage } from "./UserMessage";
import { UserPrompt } from "./UserPrompt";
import { useChatConfig } from "./useChatConfig";
import { BotActions } from "./BotActions";
import { CopyBotMessage } from "./CopyBotMessage";
import { RunZapBotMessage } from "./RunZapBotMessage";
import { cn } from "utils/cn";
import { useMemo } from "react";
import { buildBlockDisplayStyle } from "../lib/buildBlockDisplayStyle";

function noOp() {
  return;
}

const StyledChatbot: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div"> & {
    $blockHeights: { height: string; maxHeight: string };
  }
> = ({ $blockHeights, ...props }) => (
  <div
    {...props}
    style={{ height: $blockHeights.height, maxHeight: $blockHeights.maxHeight }}
    className={cn("flex flex-col justify-end gap-5", props.className)}
  />
);

const StyledMessagesScroll: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn("flex flex-col-reverse overflow-y-auto", props.className)}
  />
);

const StyledMessages: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"div">
> = (props) => (
  <div
    {...props}
    className={cn("flex flex-col gap-5 px-5 pt-5", props.className)}
  />
);

export function EditingChatbotPrompt({
  config,
}: {
  config: ChatbotBlock["config"];
}) {
  const {
    blockId,
    chatbotName,
    defaultMessage,
    defaultPrompt,
    directive,
    displayDisclosure,
    disclosureText,
    isStaticMode,
    model,
    placeholder,
    triggers,
  } = useChatConfig(config);

  const blockDisplayStyle = useMemo(
    () =>
      buildBlockDisplayStyle({
        height: config.style?.height,
        heightValue: config.style?.heightValue,
      }),
    [config.style?.heightValue, config.style?.height]
  );

  const renderDemoMessages = () =>
    triggers.length > 0 && (
      <>
        <UserMessage>
          <Badge color="white">
            Prompt input provided to the response above
          </Badge>
        </UserMessage>
        <BotMessage chatbotName={chatbotName}>
          <BotResponse>
            <Badge color="white">
              Response generated to the prompt input above
            </Badge>
          </BotResponse>
          {triggers.length > 0 && (
            <BotActions>
              {triggers.map((trigger) =>
                trigger.onClick === "copy" ? (
                  <CopyBotMessage
                    key={trigger.id}
                    buttonText={trigger.buttonText}
                    message={{
                      role: "assistant",
                      content: "This is a demo response",
                    }}
                    onClick={function noop() {
                      // do nothing (but this is where you could trigger a notification)
                    }}
                  />
                ) : (
                  <RunZapBotMessage
                    key={trigger.id}
                    buttonText={trigger.buttonText}
                    message={{
                      role: "assistant",
                      content: "This is a demo response",
                    }}
                    onClick={function noop() {
                      // do nothing (but this is where we'd trigger the block action)
                    }}
                  />
                )
              )}
            </BotActions>
          )}
        </BotMessage>
      </>
    );

  return (
    <StyledChatbot $blockHeights={blockDisplayStyle}>
      <StyledMessagesScroll>
        <StyledMessages>
          {isStaticMode ? (
            <>
              <BotMessage chatbotName={chatbotName} isHidden>
                <BotResponse>{directive}</BotResponse>
              </BotMessage>
              <BotMessage
                chatbotName={chatbotName}
                displayDisclosure={displayDisclosure}
                disclosureText={disclosureText}
              >
                <BotResponse>{defaultMessage}</BotResponse>
              </BotMessage>
              {renderDemoMessages()}
              <UserPrompt
                isDisabled
                messages={[]}
                blockId={blockId}
                placeholder={placeholder}
                onChatComplete={noOp}
                onUsage={noOp}
                model={model}
              />
            </>
          ) : (
            <>
              <BotMessage chatbotName={chatbotName} isHidden>
                <BotResponse>{directive}</BotResponse>
              </BotMessage>
              <UserMessage isHidden>{defaultPrompt}</UserMessage>
              <BotMessage
                chatbotName={chatbotName}
                displayDisclosure={displayDisclosure}
                disclosureText={disclosureText}
              >
                <BotResponse>
                  <Badge color="white">Greeting generated from prompt</Badge>
                </BotResponse>
              </BotMessage>
              {renderDemoMessages()}
              <UserPrompt
                isDisabled
                messages={[]}
                blockId={blockId}
                placeholder={placeholder}
                onChatComplete={noOp}
                onUsage={noOp}
                model={model}
              />
            </>
          )}
        </StyledMessages>
      </StyledMessagesScroll>
    </StyledChatbot>
  );
}
